import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

// Shared "Enable QR Code" field for admin edit forms (Products, Bubble Tea
// Menu Items, Instant Cakes). Catalogue-agnostic: the parent form supplies
// the current qr_enabled/qr_token state, an onChange handler, the qr-image
// URL to render, and the refresh callback to invoke after the user confirms
// the destructive "Refresh QR Code" warning. This component does not know
// (and does not need to know) which catalogue it belongs to.

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #1C1511;
  cursor: pointer;
`;

const QrPreview = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

const QrImage = styled.img`
  width: 160px;
  height: 160px;
  border: 1px solid #E8DDD0;
  border-radius: 6px;
  background: #FFFFFF;
  object-fit: contain;
`;

const RefreshButton = styled.button`
  background: #D4A017;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 14px;
  align-self: flex-start;

  &:hover {
    background: #b8890f;
  }
`;

const QrPendingHint = styled.p`
  font-size: 13px;
  color: #6B5E4E;
  margin: 0;
  font-style: italic;
`;

const QrFailedHint = styled.p`
  color: #991b1b;
  font-size: 13px;
  margin: 0;
`;

const InactiveNotice = styled.p`
  background: #fef3c7;
  color: #92400e;
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 13px;
  margin: 0;
`;

export interface QrCodeFieldProps {
  /** Current qr_enabled value for the catalogue row being edited. */
  qrEnabled: boolean;
  /** Current qr_token value (or null/undefined if none has been generated yet). */
  qrToken?: string | null;
  /** Called with the new checked state whenever the "Enable QR Code" checkbox changes. */
  onQrEnabledChange: (enabled: boolean) => void;
  /** Base URL for the QR image (the parent builds the catalogue-specific admin qr-image endpoint URL). */
  qrImageUrl: string;
  /** Called after the user confirms the "Refresh QR Code" warning. */
  onRefresh: () => void | Promise<void>;
  /** Whether the linked catalogue row is currently active/available to customers. Defaults to true so callers that don't pass it see no change in behavior. */
  itemActive?: boolean;
  /** True while a refresh request triggered via onRefresh is in flight. Disables the Refresh button and shows a loading label. Defaults to false. */
  isRefreshing?: boolean;
  /** True when a previous attempt to fetch/display the QR image failed permanently; renders a distinct "Failed to load QR code" state instead of "Loading...". Defaults to false. */
  failed?: boolean;
}

const QrCodeField: React.FC<QrCodeFieldProps> = ({
  qrEnabled,
  qrToken,
  onQrEnabledChange,
  qrImageUrl,
  onRefresh,
  itemActive = true,
  isRefreshing = false,
  failed = false,
}) => {
  const { t } = useTranslation();

  // Tracks a browser-native <img> onError (e.g. the fetch resolved HTTP 200
  // but the bytes aren't a decodable image) -- a separate failure mode from
  // the `failed` prop above, which only covers the parent's axios-catch
  // (the blob fetch itself failing). Both should render the identical
  // "Failed to load QR code" hint. Reset whenever the underlying image
  // source changes so a stale failure from a past load doesn't persist
  // across a subsequent successful refresh.
  const [imgLoadFailed, setImgLoadFailed] = useState(false);

  useEffect(() => {
    setImgLoadFailed(false);
  }, [qrToken, qrImageUrl]);

  const handleRefreshClick = () => {
    const confirmed = window.confirm(t('admin.qrCodeField.refreshConfirm'));
    if (confirmed) {
      onRefresh();
    }
  };

  const handleCheckboxChange = (checked: boolean) => {
    if (qrToken && checked !== qrEnabled) {
      const confirmed = window.confirm(t('admin.qrCodeField.enabledChangeConfirm'));
      if (!confirmed) return;
    }
    onQrEnabledChange(checked);
  };

  // Cache-busted via a `?v={qr_token}`-style param so the browser always
  // fetches the current image after a refresh -- except for blob:/data:
  // URLs (e.g. a parent that fetched the admin-gated qr-image endpoint via
  // authenticated axios and handed us an object URL), which are already
  // unique per fetch and whose format a query-string suffix would corrupt.
  const isObjectOrDataUrl = /^(blob:|data:)/.test(qrImageUrl);
  const displayUrl = isObjectOrDataUrl ? qrImageUrl : `${qrImageUrl}?v=${qrToken}`;

  // The <img> DOM node is genuinely unmounted/remounted on every regenerate
  // (it's keyed by `qrToken` below), so React attaches a fresh `onError`
  // closure directly to each specific DOM node, and that node's dispatcher
  // reads only that node's own last-committed props at fire time. When the
  // key changes, the OLD node is discarded -- but a still-in-flight native
  // image request against it can still fire a late `error` event targeting
  // that OLD node, which invokes the OLD node's own frozen `onError` closure
  // (closed over the OLD `displayUrl`) while calling the CURRENT, live
  // `setImgLoadFailed` (owned by this QrCodeField instance, which never
  // unmounts -- only the child `<img>` does). Without this guard, that
  // late/stale event would incorrectly fail the brand-new, valid image.
  // Track the currently-rendered `displayUrl` in a ref so the `onError`
  // handler -- which closes over the `displayUrl` from the render that
  // created it -- can detect and ignore a stale error instead of incorrectly
  // failing the current, actually-valid image.
  const currentDisplayUrlRef = useRef(displayUrl);
  currentDisplayUrlRef.current = displayUrl;

  return (
    <FieldGroup>
      <CheckboxLabel>
        <input
          type="checkbox"
          checked={qrEnabled}
          onChange={(e) => handleCheckboxChange(e.target.checked)}
        />
        {t('admin.qrCodeField.enableLabel')}
      </CheckboxLabel>

      {qrEnabled && !itemActive && (
        <InactiveNotice>
          {t('admin.qrCodeField.inactiveNotice')}
        </InactiveNotice>
      )}

      {qrEnabled && qrToken && qrImageUrl && !failed && !imgLoadFailed && (
        <QrPreview>
          <QrImage
            key={qrToken}
            src={displayUrl}
            alt={t('admin.qrCodeField.qrImageAlt')}
            onError={() => {
              // Ignore a stale error from an image request that has since
              // been superseded by a newer `displayUrl` (e.g. a rapid
              // second regenerate landing before the first image's error
              // event resolves) -- only fail the state if this error still
              // corresponds to the currently-rendered image.
              if (displayUrl === currentDisplayUrlRef.current) {
                setImgLoadFailed(true);
              }
            }}
          />
          <RefreshButton type="button" onClick={handleRefreshClick} disabled={isRefreshing}>
            {isRefreshing ? t('admin.qrCodeField.refreshingButton') : t('admin.qrCodeField.refreshButton')}
          </RefreshButton>
        </QrPreview>
      )}

      {qrEnabled && qrToken && (failed || imgLoadFailed) && (
        <QrFailedHint>{t('admin.qrCodeField.failedHint')}</QrFailedHint>
      )}

      {qrEnabled && qrToken && !qrImageUrl && !failed && !imgLoadFailed && (
        <QrPendingHint>{t('admin.qrCodeField.pendingHintLoading')}</QrPendingHint>
      )}

      {qrEnabled && !qrToken && (
        <QrPendingHint>{t('admin.qrCodeField.pendingHintWillGenerate')}</QrPendingHint>
      )}
    </FieldGroup>
  );
};

export default QrCodeField;
